import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  Grid,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
} from 'lucide-react';
import { getInventoryItems } from '../firebase/inventoryService';

const StockHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [categories, setCategories] = useState([]);

  // Load inventory data
  const loadData = useCallback(async () => {
    setLoading(true);
    const inventoryResult = await getInventoryItems();
    if (inventoryResult.success) {
      setInventory(prevInventory => {
        // Intelligent merge - protect critical inventory fields
        const existingMap = new Map(prevInventory.map(item => [item.id, item]));
        
        return inventoryResult.data.map(newItem => {
          const existing = existingMap.get(newItem.id);
          
          if (!existing) {
            return newItem; // New item, use as-is
          }
          
          // PROTECT IMMUTABLE FIELDS
          return {
            ...newItem,
            // Critical fields that should NEVER change
            name: existing.name,
            category: existing.category,
            // Protect batches array from being emptied
            batches: (newItem.batches && newItem.batches.length > 0) 
              ? newItem.batches 
              : existing.batches || [],
            // Protect stock from incorrect updates
            stock: newItem.stock !== undefined ? newItem.stock : existing.stock,
          };
        });
      });
      
      // Extract unique categories
      const uniqueCategories = [...new Set(inventoryResult.data.map(item => item.category || 'Uncategorized'))];
      setCategories(uniqueCategories);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get filtered products
  const filteredProducts = inventory.filter(item => {
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemCode?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get selected product details
  const selectedProductData = selectedProduct 
    ? inventory.find(item => item.id === selectedProduct)
    : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Stock History & Batches
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View stock movements and batch details with FIFO tracking
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSelectedProduct('');
                  }}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={!selectedCategory}>
                <InputLabel>Product</InputLabel>
                <Select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  label="Product"
                >
                  <MenuItem value="">All Products</MenuItem>
                  {filteredProducts.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} ({product.category})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                placeholder="Search by category name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stock Batches (if product selected) */}
      {selectedProductData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Stock Batches - {selectedProductData.name}
              {selectedProductData.itemCode && (
                <Typography component="span" variant="body2" color="primary.main" sx={{ ml: 1 }}>
                  ({selectedProductData.itemCode})
                </Typography>
              )}
              {(selectedProductData.stock || 0) === 0 && (
                <Chip label="Out of Stock" size="small" color="error" sx={{ ml: 1, verticalAlign: 'middle' }} />
              )}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Stock
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {selectedProductData.trackInML
                      ? `${selectedProductData.stock || 0} ml`
                      : `${selectedProductData.stock || 0} units`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Average Cost
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    LKR {(selectedProductData.averageCost || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Retail Price
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    LKR {(selectedProductData.retailPrice || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Batches
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {(selectedProductData.batches || []).length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>Transfer Date</TableCell>
                    <TableCell>GRN #</TableCell>
                    <TableCell>Transfer #</TableCell>
                    <TableCell align="right">Cost Price</TableCell>
                    <TableCell align="right">Qty Purchased</TableCell>
                    <TableCell align="right">Qty Remaining</TableCell>
                    <TableCell align="right">Total Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedProductData.batches || [])
                    .sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate))
                    .map((batch, index) => (
                      <TableRow key={batch.batchId} hover>
                        <TableCell>
                          <Typography variant="caption" fontWeight={600}>
                            Batch {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>{batch.purchaseDate}</TableCell>
                        <TableCell>
                          <Chip 
                            label={batch.purchaseInvoiceNumber || '—'} 
                            size="small" 
                            variant="outlined"
                            color="info"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={batch.transferNumber || '—'} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            LKR {(batch.costPrice || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={batch.quantityPurchased} 
                            size="small" 
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={batch.quantityRemaining === 0 ? 'Out of Stock' : batch.quantityRemaining} 
                            size="small"
                            color={batch.quantityRemaining > 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            LKR {(batch.costPrice * batch.quantityRemaining).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  {(!selectedProductData.batches || selectedProductData.batches.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No batches found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* All Products Overview - Mapped by Categories */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
            {searchTerm ? `Search Results for "${searchTerm}"` : 'Stock Overview by Category'}
          </Typography>

          {searchTerm ? (
            // Search Results - Grouped by Category
            <Box>
              {categories
                .filter(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(category => {
                  const categoryProducts = inventory.filter(item => item.category === category);
                  return (
                    <Box key={category} sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'primary.main' }}>
                        {category} ({categoryProducts.length} products)
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell>Item Code</TableCell>
                              <TableCell align="right">Stock</TableCell>
                              <TableCell align="right">Avg Cost</TableCell>
                              <TableCell align="right">Retail Price</TableCell>
                              <TableCell align="right">Wholesale Price</TableCell>
                              <TableCell align="center">Batches</TableCell>
                              <TableCell align="right">Stock Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {categoryProducts.map((product) => (
                              <TableRow 
                                key={product.id} 
                                hover
                                sx={{ cursor: 'pointer', opacity: (product.stock || 0) === 0 ? 0.7 : 1 }}
                                onClick={() => setSelectedProduct(product.id)}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>
                                        {product.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {product.category || 'Uncategorized'}
                                      </Typography>
                                    </Box>
                                    {(product.stock || 0) === 0 && (
                                      <Chip label="Out of Stock" size="small" color="error" variant="outlined" />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="primary.main">
                                    {product.itemCode || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip 
                                    label={product.trackInML ? `${product.stock || 0} ml` : (product.stock || 0)}
                                    size="small"
                                    color={product.stock > 0 ? 'success' : 'error'}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">
                                    {(product.averageCost || 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600}>
                                    {(product.retailPrice || 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600}>
                                    {(product.wholesalePrice || 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={(product.batches || []).length} 
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600}>
                                    {((product.averageCost || 0) * (product.stock || 0)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              {categories.filter(cat => cat.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No categories found matching "{searchTerm}"
                </Typography>
              )}
            </Box>
          ) : (
            // Default View - All Categories with Products
            <Box>
              {categories.map(category => {
                const categoryProducts = inventory.filter(item => item.category === category);
                return (
                  <Box key={category} sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'primary.main' }}>
                      {category} ({categoryProducts.length} products)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Item Code</TableCell>
                            <TableCell align="right">Stock</TableCell>
                            <TableCell align="right">Avg Cost (LKR)</TableCell>
                            <TableCell align="right">Retail Price (LKR)</TableCell>
                            <TableCell align="right">Wholesale Price (LKR)</TableCell>
                            <TableCell align="center">Batches</TableCell>
                            <TableCell align="right">Stock Value (LKR)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryProducts.map((product) => (
                            <TableRow 
                              key={product.id} 
                              hover
                              sx={{ cursor: 'pointer', opacity: (product.stock || 0) === 0 ? 0.7 : 1 }}
                              onClick={() => setSelectedProduct(product.id)}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {product.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {product.category || 'Uncategorized'}
                                    </Typography>
                                  </Box>
                                  {(product.stock || 0) === 0 && (
                                    <Chip label="Out of Stock" size="small" color="error" variant="outlined" />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="primary.main">
                                  {product.itemCode || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={product.trackInML ? `${product.stock || 0} ml` : (product.stock || 0)}
                                    size="small"
                                    color={product.stock > 0 ? 'success' : 'error'}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {(product.averageCost || 0).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600}>
                                  {(product.retailPrice || 0).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600}>
                                  {(product.wholesalePrice || 0).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={(product.batches || []).length} 
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600}>
                                  {((product.averageCost || 0) * (product.stock || 0)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockHistory;

